import { Stack, StackProps } from '@chakra-ui/react';
import { RequireOnly } from '@pelicargo/types';
import { Show, useIsoStates } from '@pelicargo/ui';
import { COUNTRIES, modelUtils } from '@pelicargo/utils';

import { formatCargoOrigin, useCargoOriginForm } from '../../hooks/request/useCargoOriginForm';
import { useUpdateRequest } from '../../hooks/request/useUpdate';
import { Request } from '../../utils/types';
import { SimpleForm } from '../SimpleForm';
import { SimpleInput } from '../SimpleInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimplePopoverUnifiedInput } from '../SimpleUnifiedPopoverInput';
import { PickupAirportDistances } from './PickupAirportDistances';

type Props = StackProps & {
  request?: RequireOnly<Request, 'id' | 'cargo_origin' | 'origin_airports' | 'freight_services'>;
};

export const CargoOriginForm = ({ request, ...rest }: Props) => {
  const { mutate } = useUpdateRequest(request?.id);
  const { methods } = useCargoOriginForm(request?.cargo_origin);

  const handleValid = async (fields: Record<string, any>) => {
    await mutate({
      cargo_origin: formatCargoOrigin(fields, methods.getValues()),
    });
  };

  const states = useIsoStates(methods.control);

  return (
    <Stack direction="column" spacing={0} {...rest}>
      <SimpleForm defaultValues={request?.cargo_origin} onValid={handleValid} {...methods}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <SimplePopoverUnifiedInput
            flex={1}
            label="Address"
            names={['recipient', 'line_one', 'line_two', 'country', 'state', 'city', 'zip_code']}
            joiner=", "
            tabIndex={0}
            isHorizontal
          >
            <SimpleInput name="recipient" label="Business name or recipient" ignoreOnBlur />
            <SimpleInput name="line_one" label="Line 1" ignoreOnBlur />
            <SimpleInput name="line_two" label="Line 2" ignoreOnBlur />
            <SimpleReactSelect
              name="country"
              label="Country *"
              variant="outline"
              options={COUNTRIES.map((country) => ({
                label: country?.name,
                value: country?.code,
              }))}
            />
            <SimpleReactSelect name="state" label="State" variant="outline" options={states} />
            <SimpleInput name="city" label="City" ignoreOnBlur />
            <SimpleInput name="zip_code" label="Zip/Postal Code" ignoreOnBlur />
          </SimplePopoverUnifiedInput>
        </Stack>
      </SimpleForm>
      <Show if={modelUtils.request.requiresInland(request)}>
        <PickupAirportDistances
          zipCode={request?.cargo_origin?.zip_code}
          country={request?.cargo_origin?.country}
          originAirports={request?.origin_airports}
        />
      </Show>
    </Stack>
  );
};
